import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArchiveModalProps, ArchiveData } from "./types";

/**
 * Generic archive modal component.
 * Handles archive reason selection and optional notes/duplicate ID.
 */
export const ArchiveModal: React.FC<ArchiveModalProps> = ({
  isOpen,
  onClose,
  onArchive,
  isLoading,
  entityName,
  archiveReasons,
  showDuplicateField = false,
  showGdprWarning = false,
  customReasonInfoBox,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveReasonNote, setArchiveReasonNote] = useState("");
  const [duplicateOfId, setDuplicateOfId] = useState("");
  const [transitionNote, setTransitionNote] = useState("");

  const resetForm = () => {
    setArchiveReason("");
    setArchiveReasonNote("");
    setDuplicateOfId("");
    setTransitionNote("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleArchive = () => {
    if (!archiveReason) return;
    if (archiveReason === "other" && !archiveReasonNote.trim()) return;
    if (showDuplicateField && archiveReason === "duplicate" && !duplicateOfId)
      return;

    const data: ArchiveData = {
      archive_reason: archiveReason,
      archive_reason_note: archiveReasonNote || null,
      note: transitionNote || "",
    };

    if (showDuplicateField) {
      data.duplicate_of_id =
        archiveReason === "duplicate" ? parseInt(duplicateOfId) : null;
    }

    onArchive(data);
    resetForm();
  };

  const isSubmitDisabled =
    !archiveReason ||
    isLoading ||
    (archiveReason === "other" && !archiveReasonNote.trim()) ||
    (showDuplicateField && archiveReason === "duplicate" && !duplicateOfId);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "24px",
          width: "100%",
          maxWidth: "480px",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h3
          style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: "600" }}
        >
          {t("recruiting:archive.title", { entity: entityName })}
        </h3>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "500",
              marginBottom: "4px",
            }}
          >
            {t("recruiting:archive.reasonLabel")} *
          </label>
          <select
            value={archiveReason}
            onChange={(e) => setArchiveReason(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
            }}
          >
            <option value="">{t("recruiting:archive.selectReason")}</option>
            {Object.entries(archiveReasons).map(([key, value]) => (
              <option key={key} value={key}>
                {value.label}
              </option>
            ))}
          </select>
        </div>

        {archiveReason === "other" && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "4px",
              }}
            >
              {t("recruiting:archive.reasonNote")} *
            </label>
            <textarea
              value={archiveReasonNote}
              onChange={(e) => setArchiveReasonNote(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder={t("recruiting:archive.reasonPlaceholder")}
              rows={3}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                resize: "vertical",
              }}
            />
          </div>
        )}

        {showDuplicateField && archiveReason === "duplicate" && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "4px",
              }}
            >
              {t("recruiting:archive.mainProfileId")} *
            </label>
            <input
              type="number"
              value={duplicateOfId}
              onChange={(e) => setDuplicateOfId(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder={t("recruiting:archive.candidateIdPlaceholder")}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
              }}
            />
          </div>
        )}

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "500",
              marginBottom: "4px",
            }}
          >
            {t("recruiting:archive.additionalNote")}
          </label>
          <textarea
            value={transitionNote}
            onChange={(e) => setTransitionNote(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={t("recruiting:archive.notePlaceholder")}
            rows={2}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
              resize: "vertical",
            }}
          />
        </div>

        {showGdprWarning && archiveReason === "gdpr_request" && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fef3c7",
              borderRadius: "6px",
              marginBottom: "16px",
              fontSize: "13px",
              color: "#92400e",
            }}
          >
            <strong>{t("recruiting:archive.warning")}:</strong>{" "}
            {t("recruiting:archive.gdprWarning", {
              entity: entityName.toLowerCase(),
            })}
          </div>
        )}

        {customReasonInfoBox &&
          archiveReason === customReasonInfoBox.reason && (
            <div
              style={{
                padding: "12px",
                backgroundColor: customReasonInfoBox.bgColor,
                borderRadius: "6px",
                marginBottom: "16px",
                fontSize: "13px",
                color: customReasonInfoBox.textColor,
              }}
            >
              {customReasonInfoBox.message}
            </div>
          )}

        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}
        >
          <button
            onClick={handleClose}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              backgroundColor: "white",
              cursor: "pointer",
            }}
          >
            {t("common:actions.cancel")}
          </button>
          <button
            onClick={handleArchive}
            disabled={isSubmitDisabled}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: "#ef4444",
              color: "white",
              cursor: isSubmitDisabled ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading
              ? t("recruiting:archive.archiving")
              : t("recruiting:archive.title", { entity: entityName })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArchiveModal;
